import React, { useState } from 'react';
import {
    X, Clock, Search, Lock, ShoppingCart, CheckCircle, AlertCircle,
    ChevronDown, ChevronRight, Eye, XCircle, ArrowDown, TrendingDown,
    History, Sparkles, MapPin
} from 'lucide-react';

// Модальное окно истории позиции
const PositionHistoryModal = ({ isOpen, onClose, positionId, history }) => {
    const [expandedEvents, setExpandedEvents] = useState({});

    if (!isOpen) return null;

    const toggleEvent = (index) => {
        setExpandedEvents(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    // Группируем события по типам
    const groupedHistory = history?.reduce((acc, event, index) => {
        const eventWithIndex = { ...event, originalIndex: index };

        if (!acc[event.event_type]) {
            acc[event.event_type] = [];
        }
        acc[event.event_type].push(eventWithIndex);
        return acc;
    }, {}) || {};

    // Получаем иконку для типа события
    const getEventIcon = (eventType) => {
        const iconMap = {
            'search_started': <Search className="text-blue-500" size={20} />,
            'bron_found': <Lock className="text-yellow-500" size={20} />,
            'search_position_checked': <Eye className="text-gray-500" size={20} />,
            'search_position_occupied': <XCircle className="text-red-500" size={20} />,
            'search_position_free': <CheckCircle className="text-green-500" size={20} />,
            'search_going_deeper': <ArrowDown className="text-purple-500" size={20} />,
            'search_completed': <Sparkles className="text-green-500" size={20} />,
            'activation': <Sparkles className="text-blue-500" size={20} />,
            'child_placed': <MapPin className="text-teal-500" size={20} />,
            'reward_earned': <Sparkles className="text-yellow-500" size={20} />,
            'clone_placed': <Sparkles className="text-purple-500" size={20} />,
            'position_closed': <CheckCircle className="text-green-500" size={20} />,
        };
        return iconMap[eventType] || <Clock className="text-gray-500" size={20} />;
    };

    // Получаем цвет для типа события
    const getEventColor = (eventType) => {
        const colorMap = {
            'search_started': 'blue',
            'bron_found': 'yellow',
            'search_position_checked': 'gray',
            'search_position_occupied': 'red',
            'search_position_free': 'green',
            'search_going_deeper': 'purple',
            'search_completed': 'green',
            'activation': 'blue',
            'child_placed': 'teal',
            'reward_earned': 'yellow',
            'clone_placed': 'purple',
            'position_closed': 'green',
        };
        return colorMap[eventType] || 'gray';
    };

    // Получаем название типа события
    const getEventTypeName = (eventType) => {
        const nameMap = {
            'search_started': '🔍 Начало поиска',
            'bron_found': '🎯 Найдена бронь',
            'search_position_checked': '👁️ Проверка позиции',
            'search_position_occupied': '❌ Позиция занята',
            'search_position_free': '✅ Свободная позиция',
            'search_going_deeper': '⬇️ Переход вглубь',
            'search_completed': '🎉 Поиск завершен',
            'activation': '🚀 Активация',
            'child_placed': '👥 Партнер размещен',
            'reward_earned': '💰 Вознаграждение',
            'clone_placed': '🎰 Клон установлен',
            'position_closed': '🎉 Позиция закрыта',
        };
        return nameMap[eventType] || eventType;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-fade-in">
                {/* Заголовок */}
                <div className="bg-gradient-to-r from-blue-500 to-teal-500 p-6 text-white">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                                <History size={24} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold">История позиции</h3>
                                <p className="text-blue-100 text-sm">Полная история событий и поиска места</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Контент */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                    {!history || history.length === 0 ? (
                        <div className="text-center py-12">
                            <History size={48} className="text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500">История событий пуста</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Хронологический список всех событий */}
                            <div className="space-y-3">
                                {history.map((event, index) => {
                                    const color = getEventColor(event.event_type);
                                    const isExpanded = expandedEvents[index];
                                    const hasMetaData = event.meta_data && Object.keys(event.meta_data).length > 0;

                                    return (
                                        <div
                                            key={index}
                                            className={`border-l-4 border-${color}-500 bg-${color}-50 rounded-r-xl overflow-hidden transition-all`}>
                                            <div
                                                className={`p-4 ${hasMetaData ? 'cursor-pointer hover:bg-opacity-80' : ''}`}
                                                onClick={() => hasMetaData && toggleEvent(index)}>
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-start space-x-3 flex-1">
                                                        <div className="mt-1">
                                                            {getEventIcon(event.event_type)}
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <span className={`text-xs font-semibold text-${color}-600`}>
                                                                    {getEventTypeName(event.event_type)}
                                                                </span>
                                                                {event.created_at && (
                                                                    <span className="text-xs text-gray-400">
                                                                        {new Date(event.created_at).toLocaleString('ru-RU')}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className={`text-sm text-${color}-800`}>
                                                                {event.description}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {hasMetaData && (
                                                        <button className="ml-2">
                                                            {isExpanded ? (
                                                                <ChevronDown size={20} className={`text-${color}-500`} />
                                                            ) : (
                                                                <ChevronRight size={20} className={`text-${color}-500`} />
                                                            )}
                                                        </button>
                                                    )}
                                                </div>

                                                {/* Дополнительная информация */}
                                                {isExpanded && hasMetaData && (
                                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                                            {Object.entries(event.meta_data).map(([key, value]) => (
                                                                <div key={key} className="flex justify-between">
                                                                    <span className="text-gray-600 font-medium">
                                                                        {key.replace(/_/g, ' ')}:
                                                                    </span>
                                                                    <span className={`text-${color}-700 font-semibold`}>
                                                                        {typeof value === 'boolean'
                                                                            ? (value ? 'Да' : 'Нет')
                                                                            : String(value)
                                                                        }
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Статистика поиска */}
                            {groupedHistory['search_completed'] && groupedHistory['search_completed'].length > 0 && (
                                <div className="mt-6 bg-gradient-to-r from-blue-50 to-teal-50 rounded-xl p-4 border border-blue-200">
                                    <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                                        <Search size={18} />
                                        Статистика поиска
                                    </h4>
                                    <div className="grid grid-cols-3 gap-4">
                                        {groupedHistory['search_completed'][0].meta_data.total_checks && (
                                            <div className="text-center">
                                                <p className="text-2xl font-bold text-blue-600">
                                                    {groupedHistory['search_completed'][0].meta_data.total_checks}
                                                </p>
                                                <p className="text-xs text-gray-600">Проверено позиций</p>
                                            </div>
                                        )}
                                        {groupedHistory['search_completed'][0].meta_data.final_depth && (
                                            <div className="text-center">
                                                <p className="text-2xl font-bold text-purple-600">
                                                    {groupedHistory['search_completed'][0].meta_data.final_depth}
                                                </p>
                                                <p className="text-xs text-gray-600">Глубина поиска</p>
                                            </div>
                                        )}
                                        {groupedHistory['search_completed'][0].meta_data.method && (
                                            <div className="text-center">
                                                <p className="text-sm font-bold text-green-600">
                                                    {groupedHistory['search_completed'][0].meta_data.method === 'bron'
                                                        ? '🎯 По брони'
                                                        : '🔍 Автоматически'}
                                                </p>
                                                <p className="text-xs text-gray-600">Метод размещения</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Футер */}
                <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t">
                    <p className="text-sm text-gray-600">
                        Всего событий: <span className="font-semibold">{history?.length || 0}</span>
                    </p>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gradient-to-r from-blue-500 to-teal-500 text-white rounded-lg hover:shadow-lg transition font-medium">
                        Закрыть
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PositionHistoryModal;
